import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/core/utils';
import { useIsMobile } from '@/hooks/ui/use-mobile';
import TodayShiftPlanCard from '@/components/shifts/TodayShiftPlanCard';
import EmployeePresenceCards from './EmployeePresenceCards';
import ScheduleOverviewCard from './ScheduleOverviewCard';
import EmployeeStatisticsCard from './EmployeeStatisticsCard';
import WeatherTimeInfoCard from './WeatherTimeInfoCard';
import { CardSkeleton, StatCardSkeleton, WeatherCardSkeleton, ChartCardSkeleton } from '@/components/ui/card-skeleton';
import QuickShiftCreator from '@/components/shifts/QuickShiftCreator';
import AddEmployeeForm from './AddEmployeeForm';
import EventFormDialog from '@/components/events/EventFormDialog';
import { BusinessDetails } from '@/types/index';
import { getBusinessStatus } from '@/lib/business/business-hours';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { shouldSkipInvalidation } from '@/lib/core/visibility-tracker';
import { useEmployeeStats } from '@/hooks/employees';
import { usePeriodStatuses } from '@/hooks/analytics/usePeriodStatuses';
import { format } from 'date-fns';
import { Users, TrendingUp, Timer, Calendar, DollarSign, MapPin, Plus, ChevronLeft, ChevronRight, Briefcase, UserCheck, Clock } from 'lucide-react';
import { UpcomingEvent } from '@/types/insights';
import { useCalendarSettings } from '@/hooks/scheduling/useCalendarSettings';
import UnifiedShiftProvider from '@/components/shifts/context/UnifiedShiftProvider';
// REMOVED: Complex performance and loading state management that was causing infinite re-renders
// The component now uses simple direct loading state checks instead

interface JobPostingStats {
  activeJobPostings: number;
  totalApplications: number;
  pendingApplications: number;
  scheduledInterviews: number;
  recentHires: number;
}


interface EmployeeInsightsTabProps {
  className?: string;
  setActiveView: (view: string) => void;
  businessDetails: BusinessDetails | null;
}

const EmployeeInsightsTabInternal: React.FC<EmployeeInsightsTabProps> = ({ className, setActiveView, businessDetails }) => {
  const { user } = useAuth();
  const isMobile = useIsMobile();


  // EMERGENCY FIX: Removed all performance monitoring and unified loading to stop infinite re-renders
  
  // Custom hooks for data fetching - NO PERFORMANCE TRACKING
  const { employeeStats, loading: employeeStatsLoading } = useEmployeeStats(businessDetails?.id);

  // Use opening hours from business data
  const openingHoursForPeriodCalc = useMemo(() => {
    return businessDetails?.opening_hours || undefined;
  }, [businessDetails?.opening_hours]);

  const { periodStatuses, plannedShifts, allShiftsAssigned, loading: periodStatusesLoading, error: periodStatusesError, refetch: refetchPeriodStatuses } = usePeriodStatuses(businessDetails?.id, openingHoursForPeriodCalc);

  // Refresh key for ScheduleOverviewCard
  const [scheduleRefreshKey, setScheduleRefreshKey] = useState(0);

  // Listen for shift changes via Supabase realtime and refresh data
  useEffect(() => {
    if (!businessDetails?.id) return;

    const channel = supabase
      .channel(`shifts-${businessDetails.id}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen for INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'shifts',
          filter: `business_id=eq.${businessDetails.id}`,
        },
        () => {
          // Skip refresh if tab just became visible (prevents refetch storm)
          if (shouldSkipInvalidation()) return;
          // Shift data changed - refresh period stats and schedule overview
          refetchPeriodStatuses();
          setScheduleRefreshKey(prev => prev + 1);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [businessDetails?.id, refetchPeriodStatuses]);

  // REMOVED: Performance tracking calls to prevent infinite re-renders
  
  // PERFORMANCE FIX: Coordinated loading state that follows proper hierarchy
  const isScheduleOverviewLoading = useMemo(() => {
    // If no business details, show loading
    if (!businessDetails?.id) {
      return true;
    }

    // If period statuses has an error, don't show loading (show error state)
    if (periodStatusesError) {
      return false;
    }

    // Hierarchy: Business data must be loaded first, then period statuses
    // Only show loading if period statuses are still loading
    const isLoading = periodStatusesLoading;

    return isLoading;
  }, [businessDetails?.id, periodStatusesLoading, periodStatusesError]);
  
  // PERFORMANCE FIX: Stable loading state management with debouncing
  const [debouncedLoading, setDebouncedLoading] = useState(isScheduleOverviewLoading);
  
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedLoading(isScheduleOverviewLoading);
    }, 100); // Small debounce to prevent loading flicker
    
    return () => clearTimeout(timeoutId);
  }, [isScheduleOverviewLoading]);
  
  const { areSidePanelsCollapsed } = useCalendarSettings(businessDetails?.id);
  
  // Local state for remaining features
  const [upcomingEventsCount, setUpcomingEventsCount] = useState(0);
  const [upcomingEvents, setUpcomingEvents] = useState<UpcomingEvent[]>([]);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [jobPostingStats, setJobPostingStats] = useState<JobPostingStats>({
    activeJobPostings: 0,
    totalApplications: 0,
    pendingApplications: 0,
    scheduledInterviews: 0,
    recentHires: 0
  });
  const [recruitmentLoading, setRecruitmentLoading] = useState(true);

  // REMOVED: Performance tracking calls to prevent infinite re-renders
  const [quickShiftDialogOpen, setQuickShiftDialogOpen] = useState(false);
  const [selectedShiftDate, setSelectedShiftDate] = useState<Date>(new Date());
  const [employees, setEmployees] = useState<any[]>([]);
  const [shiftTemplates, setShiftTemplates] = useState<any[]>([]);
  const [addEmployeeDialogOpen, setAddEmployeeDialogOpen] = useState(false);
  const [createEventDialogOpen, setCreateEventDialogOpen] = useState(false);

  const handlePresenceCardClick = () => {
    setActiveView('time-tracking');
  };

  // Handle shift creation
  const handleCreateShift = async (businessId: string, startTime: Date, endTime: Date, title: string, employeeId?: string) => {
    if (!businessDetails?.id) return;

    try {
      const { error } = await supabase
        .from('shifts')
        .insert([
          {
            business_id: businessDetails.id,
            start_time: startTime.toISOString(),
            end_time: endTime.toISOString(),
            employee_id: employeeId || null,
            title: title
          }
        ]);

      if (error) {
        console.error('Error creating shift:', error);
        throw error;
      }
    } catch (error) {
      console.error('Failed to create shift:', error);
      throw error;
    }
  };

  // Get business status for the current time
  const businessStatus = businessDetails ? getBusinessStatus(businessDetails.opening_hours) : null;




  // Fetch upcoming events data
  useEffect(() => {
    const fetchUpcomingEventsData = async () => {
      if (!businessDetails?.id) {
        setEventsLoading(false);
        return;
      }

      setEventsLoading(true);
      try {
        const { data, error } = await supabase
          .from('events')
          .select(`
            id,
            name,
            event_date,
            start_time,
            is_voluntary,
            event_assignments(
              id,
              attendance_status,
              employees(name)
            )
          `)
          .eq('business_id', businessDetails.id)
          .gte('event_date', new Date().toISOString())
          .order('event_date', { ascending: true })
          .order('start_time', { ascending: true });

        if (error) {
          console.error("Error fetching upcoming events:", error);
          return;
        }

        setUpcomingEvents(data || []);
        setUpcomingEventsCount(data?.length || 0);
        setCurrentEventIndex(0); // Reset to first event when data changes
      } catch (err) {
        console.error("Error calculating upcoming events data:", err);
      } finally {
        setEventsLoading(false);
      }
    };

    fetchUpcomingEventsData();
  }, [businessDetails?.id]);

  // Fetch job posting statistics
  useEffect(() => {
    const fetchJobPostingStats = async () => {
      if (!businessDetails?.id) {
        setRecruitmentLoading(false);
        return;
      }

      setRecruitmentLoading(true);
      try {
        // Get active job postings
        const { data: jobPostings, error: jobPostingsError } = await supabase
          .from('job_postings')
          .select('id')
          .eq('business_id', businessDetails.id)
          .eq('is_active', true);

        if (jobPostingsError) {
          console.error("Error fetching job postings:", jobPostingsError);
          return;
        }

        // Get total applications for this business
        const { data: applications, error: applicationsError } = await supabase
          .from('job_applications')
          .select('id, status, submitted_at')
          .eq('business_id', businessDetails.id);

        if (applicationsError) {
          console.error("Error fetching job applications:", applicationsError);
          return;
        }

        // Get scheduled interviews
        const { data: interviews, error: interviewsError } = await supabase
          .from('interview_schedules')
          .select('id')
          .eq('business_id', businessDetails.id)
          .eq('status', 'scheduled');

        if (interviewsError) {
          console.error("Error fetching interviews:", interviewsError);
          return;
        }

        // Calculate stats
        const pendingApplications = applications?.filter(app => 
          app.status === 'submitted' || app.status === 'screening'
        ).length || 0;

        // Recent hires (hired in last 30 days)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        const recentHires = applications?.filter(app => 
          app.status === 'hired' && 
          app.submitted_at && 
          new Date(app.submitted_at) >= thirtyDaysAgo
        ).length || 0;

        setJobPostingStats({
          activeJobPostings: jobPostings?.length || 0,
          totalApplications: applications?.length || 0,
          pendingApplications,
          scheduledInterviews: interviews?.length || 0,
          recentHires
        });

      } catch (err) {
        console.error("Error calculating job posting stats:", err);
      } finally {
        setRecruitmentLoading(false);
      }
    };

    fetchJobPostingStats();
  }, [businessDetails?.id]);

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!businessDetails?.id) return;

      try {
        const { data, error } = await supabase
          .from('employees')
          .select('id, name, email')
          .eq('business_id', businessDetails.id);

        if (error) {
          console.error('Error fetching employees:', error);
          return;
        }

        setEmployees(data || []);
      } catch (err) {
        console.error('Error fetching employees:', err);
      }
    };

    fetchEmployees();
  }, [businessDetails?.id]);

  // Fetch shift templates
  useEffect(() => {
    const fetchShiftTemplates = async () => {
      if (!businessDetails?.id) return;

      try {
        const { data, error } = await supabase
          .from('shift_templates')
          .select('*')
          .eq('business_id', businessDetails.id);

        if (error) {
          console.error('Error fetching shift templates:', error);
          return;
        }

        setShiftTemplates(data || []);
      } catch (err) {
        console.error('Error fetching shift templates:', err);
      }
    };

    fetchShiftTemplates();
  }, [businessDetails?.id]);


  // Navigation functions for events with animation
  const goToNextEvent = () => {
    if (currentEventIndex < upcomingEvents.length - 1 && !isAnimating) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentEventIndex(currentEventIndex + 1);
        setTimeout(() => setIsAnimating(false), 50);
      }, 150);
    }
  };

  const goToPreviousEvent = () => {
    if (currentEventIndex > 0 && !isAnimating) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentEventIndex(currentEventIndex - 1);
        setTimeout(() => setIsAnimating(false), 50);
      }, 150);
    }
  };

  // Get current event details
  const getCurrentEvent = () => {
    if (upcomingEvents.length === 0) return null;
    return upcomingEvents[currentEventIndex];
  };

  const currentEvent = getCurrentEvent();

  return (
    <UnifiedShiftProvider businessId={businessDetails?.id || ''}>
      <div className={cn(
        "grid gap-4",
        "grid-cols-1", // Mobile: single column
        "sm:grid-cols-2", // Small tablets: 2 columns
        "md:grid-cols-2", // Medium tablets: 2 columns
        "lg:grid-cols-4", // Desktop: 4 columns
        className
      )}>
      {/* Employee Presence Cards (replaces Total Employees) */}
      <div className="col-span-1 sm:col-span-2 md:col-span-2 lg:col-span-2 row-span-1">
        <EmployeePresenceCards 
          onClick={handlePresenceCardClick} 
          businessDetails={businessDetails}
          businessStatus={businessStatus}
        />
      </div>

      {/* Weather Info Card */}
      <div className="col-span-1 sm:col-span-2 md:col-span-2 lg:col-span-2 row-span-1">
        <WeatherTimeInfoCard 
          businessDetails={businessDetails}
          businessStatus={businessStatus}
        />
      </div>


      {/* Today's Shift Plan Card */}
      <div className="col-span-1 sm:col-span-2 md:col-span-2 lg:col-span-4">
        <TodayShiftPlanCard 
          showActionButtons={true}
          onNavigateToShiftManagement={() => setActiveView('shift-management')}
          showEditingHighlight={false}
          areSidePanelsCollapsed={areSidePanelsCollapsed}
          businessId={businessDetails?.id || null}
        />
      </div>

      {/* Schedule Overview Card */}
      <div className="col-span-1 sm:col-span-2 md:col-span-1 lg:col-span-3 row-span-2">
        <ScheduleOverviewCard
          setActiveView={setActiveView}
          businessDetails={businessDetails}
          periodStatuses={periodStatuses}
          plannedShifts={plannedShifts}
          allShiftsAssigned={allShiftsAssigned}
          isLoading={debouncedLoading}
          businessCreatedAt={businessDetails?.created_at}
          refreshKey={scheduleRefreshKey}
        />
      </div>

      {/* Quick Actions */}
      <Card className="col-span-1 sm:col-span-2 md:col-span-1 lg:col-span-1 row-span-1 sm:row-span-1 md:row-span-2 lg:row-span-2 h-full">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium">Quick Actions</CardTitle>
          <Plus className="h-5 w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent className={cn(
          isMobile ? "p-4" : "space-y-2"
        )}>
          <div className={cn(
            isMobile ? "grid grid-cols-2 gap-3" : "space-y-2"
          )}>
            {/* Employee Management */}
            <Button 
              onClick={() => setAddEmployeeDialogOpen(true)}
              variant="outline"
              className={cn(
                "justify-start gap-2",
                isMobile 
                  ? "h-12 p-3 flex-col text-xs" 
                  : "w-full h-auto p-2"
              )}
            >
              <Users className={cn(
                isMobile ? "h-5 w-5" : "h-4 w-4"
              )} />
              <span className={cn(
                "font-medium",
                isMobile ? "text-xs" : "text-sm"
              )}>Add Employee</span>
            </Button>
            
            <AddEmployeeForm
              businessId={businessDetails?.id || ''}
              isOpen={addEmployeeDialogOpen}
              onOpenChange={setAddEmployeeDialogOpen}
              onEmployeeAdded={(newEmployee) => {
                // Refresh employees list
                const fetchEmployees = async () => {
                  if (!businessDetails?.id) return;
                  try {
                    const { data, error } = await supabase
                      .from('employees')
                      .select('id, name, email')
                      .eq('business_id', businessDetails.id);
                    if (error) {
                      console.error('Error fetching employees:', error);
                      return;
                    }
                    setEmployees(data || []);
                  } catch (err) {
                    console.error('Error fetching employees:', err);
                  }
                };
                fetchEmployees();
              }}
            />
            
            {/* Event Creation */}
            <Button 
              onClick={() => setCreateEventDialogOpen(true)}
              variant="outline"
              className={cn(
                "justify-start gap-2",
                isMobile 
                  ? "h-12 p-3 flex-col text-xs" 
                  : "w-full h-auto p-2"
              )}
            >
              <Calendar className={cn(
                isMobile ? "h-5 w-5" : "h-4 w-4"
              )} />
              <span className={cn(
                "font-medium",
                isMobile ? "text-xs" : "text-sm"
              )}>Create Event</span>
            </Button>
            
            {createEventDialogOpen && (
              <EventFormDialog
                businessId={businessDetails?.id || ''}
                onClose={() => setCreateEventDialogOpen(false)}
              />
            )}
            
            {/* Quick Shift Creator Dialog */}
            <Dialog open={quickShiftDialogOpen} onOpenChange={setQuickShiftDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className={cn(
                  "justify-start gap-2",
                  isMobile 
                    ? "h-12 p-3 flex-col text-xs" 
                    : "w-full h-auto p-2"
                )}>
                  <Clock className={cn(
                    isMobile ? "h-5 w-5" : "h-4 w-4"
                  )} />
                  <span className={cn(
                    "font-medium",
                    isMobile ? "text-xs" : "text-sm"
                  )}>Create Shift</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Plus className="h-5 w-5 text-primary" />
                    Create New Shift
                  </DialogTitle>
                  <DialogDescription>
                    Create a new shift for your selected date using templates or custom settings.
                  </DialogDescription>
                </DialogHeader>
                <div className="p-6">
                  {/* Date Selection */}
                  <div className="mb-6">
                    <Label htmlFor="shift-date" className="text-sm font-medium">
                      Select Date for Shift
                    </Label>
                    <Input
                      id="shift-date"
                      type="date"
                      value={format(selectedShiftDate, 'yyyy-MM-dd')}
                      onChange={(e) => setSelectedShiftDate(new Date(e.target.value))}
                      className="mt-2 max-w-xs"
                    />
                  </div>
                  
                  <QuickShiftCreator
                    selectedDate={selectedShiftDate}
                    employees={employees}
                    templates={shiftTemplates}
                    recentTemplates={[]}
                    onCreateShift={async (businessId, startTime, endTime, title, employeeId) => {
                      await handleCreateShift(businessId, startTime, endTime, title, employeeId);
                      setQuickShiftDialogOpen(false);
                    }}
                    onTemplateUsed={() => {}}
                    businessId={businessDetails?.id || ''}
                    openingHours={businessDetails?.opening_hours || []}
                    businessHours={businessDetails?.opening_hours}
                  />
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Time Tracking */}
            <Button 
              onClick={() => setActiveView('time-tracking')}
              variant="outline"
              className={cn(
                "justify-start gap-2",
                isMobile 
                  ? "h-12 p-3 flex-col text-xs" 
                  : "w-full h-auto p-2"
              )}
            >
              <Timer className={cn(
                isMobile ? "h-5 w-5" : "h-4 w-4"
              )} />
              <span className={cn(
                "font-medium",
                isMobile ? "text-xs" : "text-sm"
              )}>Time Logs</span>
            </Button>
            
            {/* Payroll Access */}
            <Button 
              onClick={() => setActiveView('payroll')}
              variant="outline"
              className={cn(
                "justify-start gap-2",
                isMobile 
                  ? "h-12 p-3 flex-col text-xs" 
                  : "w-full h-auto p-2"
              )}
            >
              <DollarSign className={cn(
                isMobile ? "h-5 w-5" : "h-4 w-4"
              )} />
              <span className={cn(
                "font-medium",
                isMobile ? "text-xs" : "text-sm"
              )}>{isMobile ? "Payroll" : "View Payroll"}</span>
            </Button>
            
            {/* Business Settings */}
            <Button 
              onClick={() => setActiveView('google-calendar')}
              variant="outline"
              className={cn(
                "justify-start gap-2",
                isMobile 
                  ? "h-12 p-3 flex-col text-xs" 
                  : "w-full h-auto p-2"
              )}
            >
              <MapPin className={cn(
                isMobile ? "h-5 w-5" : "h-4 w-4"
              )} />
              <span className={cn(
                "font-medium",
                isMobile ? "text-xs" : "text-sm"
              )}>{isMobile ? "Settings" : "Business Settings"}</span>
            </Button>
            
            {/* Job Postings */}
            <Button 
              onClick={() => setActiveView('job-postings')}
              variant="outline"
              className={cn(
                "justify-start gap-2",
                isMobile 
                  ? "h-12 p-3 flex-col text-xs" 
                  : "w-full h-auto p-2"
              )}
            >
              <Briefcase className={cn(
                isMobile ? "h-5 w-5" : "h-4 w-4"
              )} />
              <span className={cn(
                "font-medium",
                isMobile ? "text-xs" : "text-sm"
              )}>{isMobile ? "Jobs" : "Job Postings"}</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      

      {/* 2x2 Upcoming Events Card */}
      {eventsLoading ? (
        <CardSkeleton 
          className="col-span-2 row-span-2"
          headerHeight="md"
          contentRows={6}
          contentWidths={['3/4', '1/2', 'full', '3/4', '1/3', '2/3']}
        />
      ) : (
        <Card className="col-span-1 sm:col-span-2 md:col-span-2 lg:col-span-2 row-span-2" onClick={() => setActiveView('events')} style={{ cursor: 'pointer' }}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
            <CardTitle className="text-sm font-medium">Upcoming Events</CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {upcomingEventsCount > 0 ? `${currentEventIndex + 1}/${upcomingEventsCount}` : '0/0'}
              </span>
              <Calendar className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {upcomingEventsCount === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-center">
                <Calendar className="h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-lg font-medium text-muted-foreground">No upcoming events</p>
                <p className="text-sm text-muted-foreground">Create an event to get started</p>
              </div>
            ) : currentEvent ? (
              <>
                {/* Event Navigation */}
                <div className={cn(
                  "flex items-center justify-between",
                  isMobile ? "px-2" : ""
                )}>
                  <button
                    onClick={(e) => { e.stopPropagation(); goToPreviousEvent(); }}
                    disabled={currentEventIndex === 0 || isAnimating}
                    className={cn(
                      "rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:scale-110",
                      isMobile ? "p-2 min-h-[44px] min-w-[44px]" : "p-1"
                    )}
                  >
                    <ChevronLeft className={cn(
                      isMobile ? "h-5 w-5" : "h-4 w-4"
                    )} />
                  </button>
                  <div className={`text-center flex-1 transition-all duration-300 ${
                    isAnimating ? 'opacity-0 transform scale-95' : 'opacity-100 transform scale-100'
                  }`}>
                    <h3 className={cn(
                      "font-semibold truncate",
                      isMobile ? "text-base" : "text-lg"
                    )}>{currentEvent.name}</h3>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <span className={`px-2 py-1 text-xs rounded-full transition-all duration-200 ${
                        currentEvent.is_voluntary 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {currentEvent.is_voluntary ? 'Voluntary' : 'Mandatory'}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); goToNextEvent(); }}
                    disabled={currentEventIndex === upcomingEvents.length - 1 || isAnimating}
                    className={cn(
                      "rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:scale-110",
                      isMobile ? "p-2 min-h-[44px] min-w-[44px]" : "p-1"
                    )}
                  >
                    <ChevronRight className={cn(
                      isMobile ? "h-5 w-5" : "h-4 w-4"
                    )} />
                  </button>
                </div>

                {/* Event Details */}
                <div className={cn(
                  "transition-all duration-300",
                  isMobile ? "space-y-4" : "space-y-3",
                  isAnimating ? 'opacity-0 transform translate-x-2' : 'opacity-100 transform translate-x-0'
                )}>
                  <div className={cn(
                    "flex items-center",
                    isMobile ? "flex-col space-y-1" : "justify-between"
                  )}>
                    <span className={cn(
                      "text-muted-foreground",
                      isMobile ? "text-xs" : "text-sm"
                    )}>Date & Time</span>
                    <span className={cn(
                      "font-medium",
                      isMobile ? "text-sm text-center" : "text-sm"
                    )}>
                      {format(new Date(currentEvent.event_date), 'MMM d, yyyy')}
                      {currentEvent.start_time && ` at ${currentEvent.start_time}`}
                    </span>
                  </div>
                  
                  <div className={cn(
                    "flex items-center",
                    isMobile ? "flex-col space-y-1" : "justify-between"
                  )}>
                    <span className={cn(
                      "text-muted-foreground",
                      isMobile ? "text-xs" : "text-sm"
                    )}>Attendees</span>
                    <span className={cn(
                      "font-medium",
                      isMobile ? "text-sm" : "text-sm"
                    )}>
                      {currentEvent.event_assignments?.filter((a: any) => a.attendance_status === 'confirmed').length || 0} confirmed
                    </span>
                  </div>

                  <div className={cn(
                    "flex items-center",
                    isMobile ? "flex-col space-y-1" : "justify-between"
                  )}>
                    <span className={cn(
                      "text-muted-foreground",
                      isMobile ? "text-xs" : "text-sm"
                    )}>Total Invited</span>
                    <span className={cn(
                      "font-medium",
                      isMobile ? "text-sm" : "text-sm"
                    )}>
                      {currentEvent.event_assignments?.length || 0} people
                    </span>
                  </div>
                </div>
              </>
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* Employee Statistics Card */}
      <div className="col-span-1 sm:col-span-2 md:col-span-2 lg:col-span-2 row-span-2">
        <EmployeeStatisticsCard
          employeeStats={employeeStats}
          setActiveView={setActiveView}
          loading={employeeStatsLoading}
        />
      </div>

      {/* Job Postings & Recruitment Card - 2x1 */}
      {recruitmentLoading ? (
        <ChartCardSkeleton className="col-span-2 row-span-1" />
      ) : (
        <Card className="col-span-1 sm:col-span-2 md:col-span-2 lg:col-span-2 row-span-1" onClick={() => setActiveView('job-postings')} style={{ cursor: 'pointer' }}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
            <CardTitle className="text-sm font-medium">Recruitment</CardTitle>
            <Briefcase className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent className="space-y-1">
            {jobPostingStats.activeJobPostings === 0 ? (
              /* No Active Job Postings State */
              <div className="flex flex-col items-center justify-center h-24 text-center">
                <Briefcase className="h-6 w-6 text-muted-foreground mb-1" />
                <p className="text-sm font-medium text-muted-foreground">No open job postings</p>
                <p className="text-xs text-muted-foreground">Create a job posting to start recruiting</p>
              </div>
            ) : (
              <>
                {/* Top Row Stats */}
                <div className={cn(
                  "grid gap-2",
                  isMobile ? "grid-cols-1" : "grid-cols-2"
                )}>
                  <div className={cn(
                    "text-center bg-blue-50 dark:bg-blue-950/20 rounded",
                    isMobile ? "p-3" : "p-1"
                  )}>
                    <div className={cn(
                      "font-bold text-blue-600 dark:text-blue-400",
                      isMobile ? "text-xl" : "text-lg"
                    )}>{jobPostingStats.activeJobPostings}</div>
                    <p className={cn(
                      "text-muted-foreground",
                      isMobile ? "text-sm" : "text-xs"
                    )}>Active Posts</p>
                  </div>
                  <div className={cn(
                    "text-center bg-green-50 dark:bg-green-950/20 rounded",
                    isMobile ? "p-3" : "p-1"
                  )}>
                    <div className={cn(
                      "font-bold text-green-600 dark:text-green-400",
                      isMobile ? "text-xl" : "text-lg"
                    )}>{jobPostingStats.totalApplications}</div>
                    <p className={cn(
                      "text-muted-foreground",
                      isMobile ? "text-sm" : "text-xs"
                    )}>Applications</p>
                  </div>
                </div>

                {/* Application Pipeline Metrics */}
                <div className={cn(
                  "gap-1",
                  isMobile ? "grid grid-cols-2" : "grid grid-cols-4"
                )}>
                  <div className={cn(
                    "flex flex-col items-center bg-muted/20 rounded text-center",
                    isMobile ? "p-2 min-h-[4rem]" : "p-1"
                  )}>
                    <Clock className={cn(
                      "text-orange-500 mb-0.5",
                      isMobile ? "h-4 w-4" : "h-3 w-3"
                    )} />
                    <span className={cn(
                      "font-semibold",
                      isMobile ? "text-sm" : "text-xs"
                    )}>{jobPostingStats.pendingApplications}</span>
                    <span className={cn(
                      "text-muted-foreground",
                      isMobile ? "text-xs" : "text-xs"
                    )}>Pending</span>
                  </div>

                  <div className={cn(
                    "flex flex-col items-center bg-muted/20 rounded text-center",
                    isMobile ? "p-2 min-h-[4rem]" : "p-1"
                  )}>
                    <Calendar className={cn(
                      "text-primary mb-0.5",
                      isMobile ? "h-4 w-4" : "h-3 w-3"
                    )} />
                    <span className={cn(
                      "font-semibold",
                      isMobile ? "text-sm" : "text-xs"
                    )}>{jobPostingStats.scheduledInterviews}</span>
                    <span className={cn(
                      "text-muted-foreground",
                      isMobile ? "text-xs" : "text-xs"
                    )}>Interviews</span>
                  </div>

                  <div className={cn(
                    "flex flex-col items-center bg-muted/20 rounded text-center",
                    isMobile ? "p-2 min-h-[4rem]" : "p-1"
                  )}>
                    <UserCheck className={cn(
                      "text-green-500 mb-0.5",
                      isMobile ? "h-4 w-4" : "h-3 w-3"
                    )} />
                    <span className={cn(
                      "font-semibold",
                      isMobile ? "text-sm" : "text-xs"
                    )}>{jobPostingStats.recentHires}</span>
                    <span className={cn(
                      "text-muted-foreground",
                      isMobile ? "text-xs" : "text-xs"
                    )}>Hires</span>
                  </div>

                  <div className={cn(
                    "flex flex-col items-center bg-muted/20 rounded text-center",
                    isMobile ? "p-2 min-h-[4rem]" : "p-1"
                  )}>
                    <TrendingUp className={cn(
                      "text-blue-500 mb-0.5",
                      isMobile ? "h-4 w-4" : "h-3 w-3"
                    )} />
                    <span className={cn(
                      "font-semibold",
                      isMobile ? "text-sm" : "text-xs"
                    )}>
                      {jobPostingStats.totalApplications > 0 
                        ? Math.round((jobPostingStats.recentHires / jobPostingStats.totalApplications) * 100)
                        : 0}%
                    </span>
                    <span className={cn(
                      "text-muted-foreground",
                      isMobile ? "text-xs" : "text-xs"
                    )}>Success</span>
                  </div>
                </div>

                {/* Bottom Summary */}
                <div className="text-center">
                  <div className="text-sm font-medium">
                    {jobPostingStats.pendingApplications + jobPostingStats.scheduledInterviews} 
                    <span className="text-muted-foreground font-normal"> in pipeline</span>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

        {/* TEMPORARILY DISABLE Performance Debug Panel to fix infinite loop */}
        {/* {process.env.NODE_ENV === 'development' && <DebugPanel />} */}
      </div>
    </UnifiedShiftProvider>
  );
};

// Error boundary wrapper for the component
const EmployeeInsightsTab: React.FC<EmployeeInsightsTabProps> = (props) => {
  try {
    return <EmployeeInsightsTabInternal {...props} />;
  } catch (error) {
    console.error('📋 EmployeeInsightsTab: Component error, showing fallback', error);
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <div className="text-muted-foreground">
          <p>Unable to load employee insights</p>
          <p className="text-xs mt-1">Please refresh the page</p>
        </div>
      </div>
    );
  }
};

export default EmployeeInsightsTab;